import {useEffect} from 'react';
import {useRouter} from 'next/router';

import OnboardingLayout from '@/layout/onboarding/OnboardingLayout';

import {useAppConfig} from '@/contexts/AppConfigContext';
import {useUser} from '@/contexts/UserContext';
import {useWelcomeReferredDialog} from '@/hooks/useWelcomeReferredDialog/useWelcomeReferredDialog';
import {referralService} from '@/services/referral/ReferralService';
import {tracking} from '@/services/tracking/TrackingService';
import {initApolloAndGetEmptyProps} from '@/utils/server';

import {EnterEmailSignup} from '@/components/auth/signup/EnterEmailSignup/EnterEmailSignup';
import PageInitiator from '@/components/PageInitiator';

import type {GetServerSidePropsContext} from 'next';
import type {ReactElement} from 'react';
import CountriesInfoContextProvider from '@/contexts/CountriesInfoContext';

export async function getServerSideProps(context: GetServerSidePropsContext) {
  return await initApolloAndGetEmptyProps(context);
}

const EnterEmail = () => {
  const {fbUser} = useUser();
  const {query, replace} = useRouter();
  const showWelcomeReferredDialog = useWelcomeReferredDialog();
  const {appConfig} = useAppConfig();
  const {referFriendEnabledPwa} = appConfig;

  useEffect(() => {
    setTimeout(() => {
      tracking.logEvent(tracking.events.sign_up_initiated());
    }, 300);
  }, []);

  useEffect(() => {
    if (query.referrerId && typeof query.referrerId === 'string') {
      referralService.setReferrer(query.referrerId);

      if (referFriendEnabledPwa === 'v2') {
        showWelcomeReferredDialog();
      }
    }
  }, [query, referFriendEnabledPwa, showWelcomeReferredDialog]);

  useEffect(() => {
    const handleUserClicksOwnReferralLink = async () => {
      const isUserReferrer = fbUser && fbUser.uid === query.referrerId;
      if (isUserReferrer) {
        // If the user clicks his own referral link, then redirect him to the referral page
        return await replace('/settings/refer-a-friend');
      }
    };

    handleUserClicksOwnReferralLink();
  }, [fbUser, query, replace]);

  return <EnterEmailSignup />;
};

EnterEmail.getLayout = function getLayout(page: ReactElement) {
  return (
    <PageInitiator {...page.props}>
      <CountriesInfoContextProvider>
        <OnboardingLayout pageTitle="Sign up | Ka. Web App" isLeftAligned>
          {page}
        </OnboardingLayout>
      </CountriesInfoContextProvider>
    </PageInitiator>
  );
};

export default EnterEmail;
